import { Doc, generateToDocFn } from '@dadohr/shared/domainTypes/document';
import {
  DoceboCoursePublic,
  DoceboIntegrationConfig
} from '@dadohr/shared/domainTypes/integrations/docebo';
import { DEMO_ORG_IDS } from '@dadohr/shared/domainTypes/org';
import { CF, FS } from '@dadohr/shared/versions';
import {
  store,
  useMappedLoadingValue
} from '../../../../../../services/firebase';
import { callFirebaseFunction } from '../../../../../../services/firebaseFunctions';
import {
  createDocumentListenerGetter,
  useDocumentListener
} from '../../../../../../services/firecache/documentListener';
import { now } from '../../../../../../services/time';
import { usePromise } from '../../../../../../hooks/usePromise';

export const toDoceboConfigDoc = generateToDocFn<DoceboIntegrationConfig>();

const COLLECTION = FS.doceboIntegrationConfig;
const collection = () => store().collection(COLLECTION);
export const doceboConfigStore = createDocumentListenerGetter(
  (orgId) => collection().doc(orgId),
  toDoceboConfigDoc
);

export const useDoceboConfig = (orgId: string, userId: string) => {
  return useMappedLoadingValue(
    useDocumentListener(doceboConfigStore(orgId)),
    (d) => {
      if (!d) {
        const n = now();
        const newConfig: Doc<DoceboIntegrationConfig> = {
          id: orgId,
          collection: COLLECTION,
          data: {
            orgId,
            active: false,
            createdAt: n,
            createdBy: userId,
            editedAt: n,
            editedBy: userId,

            subdomain: '',
            clientId: '',
            clientSecret: '',
            refreshToken: '',
            expiresAt: now(),
            refreshExpiresAt: now()
          }
        };
        return newConfig;
      }
      return d;
    }
  );
};

export const removeDoceboConfig = (orgId: string) => {
  return collection().doc(orgId).delete();
};

export const setupDoceboIntegration = async (
  orgId: string,
  userId: string,
  args: {
    subdomain: string;
    clientId: string;
    clientSecret: string;
    // 'PASSWORD' (default) or 'CLIENT_CREDENTIALS'. Undefined ⇒ PASSWORD.
    authMethod?: DoceboIntegrationConfig['authMethod'];
    // PASSWORD-only. Ignored for CLIENT_CREDENTIALS.
    username?: string;
    password?: string;
  }
): Promise<void> => {
  await callFirebaseFunction<{
    config: Doc<DoceboIntegrationConfig>;
  }>(CF.docebo.setup, {
    orgId,
    userId,
    args
  });
};

export const getDoceboCourses = async (
  orgId: string
): Promise<DoceboCoursePublic[]> => {
  if (DEMO_ORG_IDS.includes(orgId)) {
    // Sample content TODO
    return [];
  }
  return callFirebaseFunction<DoceboCoursePublic[]>(CF.docebo.getCourses, {
    orgId
  });
};

export const useDoceboCourses = (orgId: string) => {
  return usePromise(() => getDoceboCourses(orgId), [orgId]);
};
